/**
 * ===================================================================================
 * 📁 경로: services\frontend\src\index.js
 * 📌 프로그램명: React 애플리케이션 진입점 (Entrypoint)
 * 🧩 개요:
 * - AI 닥터큐 프론트엔드 애플리케이션을 시작하는 최초 실행 파일입니다.
 * - 메인 컴포넌트인 `<App />`을 실제 HTML DOM에 렌더링하고, 애플리케이션
 * 전반에 걸쳐 사용될 전역 컨텍스트(Context)와 설정을 초기화합니다.
 * ===================================================================================
 */
import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { setupGlobalErrorLogging, sendClientLog } from './utils/logger';
import { setupAxiosInterceptors } from './utils/axiosInterceptor';

// React Query Provider 추가
import { QueryClient, QueryClientProvider } from 'react-query';

/**
 * [Why]
 * 이 파일은 AI 닥터큐 웹 애플리케이션의 구동을 담당하는 심장과 같은 역할을 합니다.
 * 브라우저가 HTML 파일을 열었을 때, 이 스크립트가 실행되면서 우리가 만든 모든 React
 * 컴포넌트들을 실제 화면에 그려주고, 앱 전체에서 필요한 데이터 관리 기능(React Query)과
 * 안정적인 운영을 위한 에러 로깅 시스템을 설정합니다.
 *
 * [How]
 * 1.  **전역 데이터 캐시 설정**: `react-query`의 `QueryClient` 인스턴스를 생성합니다.
 * 이는 API 데이터를 효율적으로 캐싱하고 관리하여 서버 요청을 최소화하는 역할을 합니다.
 * 2.  **전역 에러 로깅 설정**: `setupGlobalErrorLogging` 함수를 호출하여, 애플리케이션
 * 전체에서 처리되지 않은 모든 에러를 감지하고 백엔드로 전송하는 시스템을 활성화합니다.
 * 3.  **애플리케이션 렌더링**: `ReactDOM.render`를 사용하여 최상위 `<App />` 컴포넌트를
 * `public/index.html` 파일의 `<div id="root"></div>` 엘리먼트 내부에 렌더링합니다.
 * 4.  **전역 컨텍스트 제공**: `<QueryClientProvider>`로 `<App />` 컴포넌트를 감싸서,
 * 애플리케이션 내의 모든 컴포넌트가 `react-query`의 데이터 관리 기능에 접근할 수
 * 있도록 합니다.
 * 5.  **개발 모드 검사**: `<React.StrictMode>`는 개발 환경에서 잠재적인 문제를 감지하고
 * 경고를 표시하여 코드의 품질을 높이는 데 도움을 줍니다.
 */

// `react-query`를 앱 전체에서 사용하기 위한 클라이언트 인스턴스를 생성합니다.
const queryClient = new QueryClient();

// 처리되지 않은 모든 자바스크립트 에러를 감지하여 백엔드로 전송하는 로거를 설정합니다.
setupGlobalErrorLogging();

// Axios Interceptor 설정: 모든 API 요청에 인증 헤더 추가 및 401 에러 시 자동 토큰 갱신
setupAxiosInterceptors();

// 사용자가 앱을 처음 로드했음을 알리는 진단 로그를 백엔드로 1회 전송합니다.
sendClientLog('info', 'app_loaded', { ts: Date.now() });

ReactDOM.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
  document.getElementById('root')
);
